import {
  KoButton,
  KoForm,
  KoLoader,
  KoModal,
  KoTable,
  KoTabs,
  KoText,
  KoTitle,
  // KoTag,
  KoToast,
  KoEmpty,
  KoPagination,
  KoSwitch
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { MonitoringAPI } from '../services/monitoring.service';
import {
  COLUMNS_ALERTS,
  COLUMNS_ALERT_HISTORY,
  // ALERT_STATUS_CONFIG,
  CHANNEL_TYPES
} from '../constants';
import AlertRuleCrud from './crud/AlertRuleCrud';
import AlertChannelCrud from './crud/AlertChannelCrud';
import MonitoringSubNav from './MonitoringSubNav';
import styles from './monitoring.module.scss';

const MOCK_ALERT_RULES = [
  { alertId: 'ar-001', name: 'High Error Rate', service: 'api-gateway', metric: 'error_rate', condition: 'gt', threshold: 5, severity: 'critical', status: 'active', enabled: true, channelId: 'ch-001', evaluationWindow: '5m', lastTriggered: '2026-02-24T08:32:00Z' },
  { alertId: 'ar-002', name: 'Slow Response Time', service: 'payment-processor', metric: 'latency_p95', condition: 'gt', threshold: 500, severity: 'medium', status: 'active', enabled: true, channelId: 'ch-001', evaluationWindow: '10m', lastTriggered: '2026-02-24T06:15:00Z' },
  { alertId: 'ar-003', name: 'Memory Usage Critical', service: 'search-engine', metric: 'memory', condition: 'gt', threshold: 90, severity: 'critical', status: 'active', enabled: true, channelId: 'ch-002', evaluationWindow: '5m', lastTriggered: '2026-02-24T07:50:00Z' },
  { alertId: 'ar-004', name: 'CPU Spike Detection', service: 'analytics-pipeline', metric: 'cpu', condition: 'gt', threshold: 85, severity: 'high', status: 'silenced', enabled: false, channelId: 'ch-001', evaluationWindow: '15m', lastTriggered: '2026-02-22T18:30:00Z' },
  { alertId: 'ar-005', name: 'Connection Pool Exhaustion', service: 'cache-layer', metric: 'connections', condition: 'gt', threshold: 8000, severity: 'critical', status: 'active', enabled: true, channelId: 'ch-002', evaluationWindow: '5m', lastTriggered: '2026-02-23T14:20:00Z' },
  { alertId: 'ar-006', name: 'Low Throughput Alert', service: 'notification-hub', metric: 'http_requests', condition: 'lt', threshold: 100, severity: 'low', status: 'resolved', enabled: true, channelId: 'ch-001', evaluationWindow: '30m', lastTriggered: '2026-02-21T10:00:00Z' }
];

const MOCK_ALERT_HISTORY = [
  { historyId: 'ah-001', alertName: 'High Error Rate', service: 'api-gateway', severity: 'critical', status: 'active', timestamp: '2026-02-24T08:32:00Z', metricValue: '7.2%', duration: '1h 28m' },
  { historyId: 'ah-002', alertName: 'Slow Response Time', service: 'payment-processor', severity: 'medium', status: 'resolved', timestamp: '2026-02-24T06:15:00Z', metricValue: '620ms', duration: '30m' },
  { historyId: 'ah-003', alertName: 'Memory Usage Critical', service: 'search-engine', severity: 'critical', status: 'active', timestamp: '2026-02-24T07:50:00Z', metricValue: '92.4%', duration: '2h 10m' },
  { historyId: 'ah-004', alertName: 'CPU Spike Detection', service: 'analytics-pipeline', severity: 'high', status: 'resolved', timestamp: '2026-02-23T22:10:00Z', metricValue: '91%', duration: '50m' },
  { historyId: 'ah-005', alertName: 'High Error Rate', service: 'search-engine', severity: 'critical', status: 'resolved', timestamp: '2026-02-23T14:20:00Z', metricValue: '8.7%', duration: '50m' }
];

const MOCK_CHANNELS = [
  { channelId: 'ch-001', _id: 'ch-001', name: 'Ops Team Slack', type: 'slack', webhookUrl: 'https://hooks.slack.com/services/T00/B00/xxx' },
  { channelId: 'ch-002', _id: 'ch-002', name: 'Critical Alerts Email', type: 'email', recipients: 'oncall@kwapio.com, devops@kwapio.com' },
  { channelId: 'ch-003', _id: 'ch-003', name: 'PagerDuty On-Call', type: 'pagerduty', integrationKey: 'pd-key-xxxxx' }
];

const MOCK_SERVICE_LIST = [
  { label: 'api-gateway', value: 'svc-001' },
  { label: 'auth-service', value: 'svc-002' },
  { label: 'payment-processor', value: 'svc-003' },
  { label: 'notification-hub', value: 'svc-004' },
  { label: 'search-engine', value: 'svc-006' },
  { label: 'analytics-pipeline', value: 'svc-008' },
  { label: 'cache-layer', value: 'svc-009' }
];

function Alerts() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [activeTab, setActiveTab] = useState('rules');
  const [alertRules, setAlertRules] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [channels, setChannels] = useState([]);
  const [totalRules, setTotalRules] = useState(0);
  const [totalHistory, setTotalHistory] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });

  const [isRuleModalOpen, setRuleModalOpen] = useState(false);
  const [isChannelModalOpen, setChannelModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [serviceList, setServiceList] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);

  const [ruleForm] = KoForm.useForm();
  const [channelForm] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/monitoring`
    : '/monitoring';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Monitoring', path: basePath },
      { title: 'Alerts' }
    ]));
    fetchServiceList();
  }, [workspaceId]);

  useEffect(() => {
    if (activeTab === 'rules') fetchRules();
    else if (activeTab === 'history') fetchHistory();
    else if (activeTab === 'channels') fetchChannels();
  }, [activeTab, pagination, workspaceId]);

  const fetchServiceList = () => {
    MonitoringAPI.getServiceList().then(
      (data) => {
        const list = (data?.data || data || []).map(s => ({ label: s.serviceName || s.name, value: s.serviceId || s._id }));
        setServiceList(list.length ? list : MOCK_SERVICE_LIST);
      },
      () => setServiceList(MOCK_SERVICE_LIST)
    );
  };

  const fetchRules = () => {
    setLoading(true);
    MonitoringAPI.getAlertRules({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_ALERT_RULES;
        setAlertRules(data);
        setTotalRules(res?.totalCount || data.length);
        setLoading(false);
      },
      () => { setAlertRules(MOCK_ALERT_RULES); setTotalRules(MOCK_ALERT_RULES.length); setLoading(false); }
    );
    MonitoringAPI.getAlertChannels().then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_CHANNELS;
        setChannelOptions(data.map(c => ({ label: c.name, value: c._id || c.channelId })));
      },
      () => setChannelOptions(MOCK_CHANNELS.map(c => ({ label: c.name, value: c.channelId })))
    );
  };

  const fetchHistory = () => {
    setLoading(true);
    MonitoringAPI.getAlertHistory({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_ALERT_HISTORY;
        setAlertHistory(data);
        setTotalHistory(res?.totalCount || data.length);
        setLoading(false);
      },
      () => { setAlertHistory(MOCK_ALERT_HISTORY); setTotalHistory(MOCK_ALERT_HISTORY.length); setLoading(false); }
    );
  };

  const fetchChannels = () => {
    setLoading(true);
    MonitoringAPI.getAlertChannels().then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_CHANNELS;
        setChannels(data);
        setLoading(false);
      },
      () => { setChannels(MOCK_CHANNELS); setLoading(false); }
    );
  };

  const handleRuleSave = (values) => {
    setLoading(true);
    MonitoringAPI.createAlertRule(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setRuleModalOpen(false);
        ruleForm.resetFields();
        setCurrentRecord(null);
        fetchRules();
        KoToast.success(currentRecord ? 'Alert rule updated.' : 'Alert rule created.');
      },
      () => { setLoading(false); KoToast.error('Failed to save alert rule.'); }
    );
  };

  const handleChannelSave = (values) => {
    setLoading(true);
    MonitoringAPI.createAlertChannel(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setChannelModalOpen(false);
        channelForm.resetFields();
        setCurrentRecord(null);
        fetchChannels();
        KoToast.success(currentRecord ? 'Channel updated.' : 'Channel created.');
      },
      () => { setLoading(false); KoToast.error('Failed to save channel.'); }
    );
  };

  const handleToggleRule = (record) => {
    MonitoringAPI.toggleAlertRule(record._id || record.alertId, { enabled: !record.enabled }).then(
      () => { fetchRules(); KoToast.success('Rule toggled.'); },
      () => KoToast.error('Failed to toggle rule.')
    );
  };

  const handleDeleteConfirm = () => {
    setLoading(true);
    const deleteCall = activeTab === 'rules'
      ? MonitoringAPI.deleteAlertRule(currentRecord._id || currentRecord.alertId)
      : MonitoringAPI.deleteAlertChannel(currentRecord._id || currentRecord.channelId);

    deleteCall.then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        if (activeTab === 'rules') fetchRules();
        else fetchChannels();
        KoToast.success('Deleted successfully.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const handleAcknowledge = (alertId) => {
    MonitoringAPI.acknowledgeAlert(alertId).then(
      () => { fetchHistory(); KoToast.success('Alert acknowledged.'); },
      () => KoToast.error('Failed to acknowledge.')
    );
  };

  const handleResolve = (alertId) => {
    MonitoringAPI.resolveAlert(alertId).then(
      () => { fetchHistory(); KoToast.success('Alert resolved.'); },
      () => KoToast.error('Failed to resolve.')
    );
  };

  const rulesColumnsWithActions = [
    ...COLUMNS_ALERTS,
    {
      label: 'Enabled', id: 'enabled', key: 'enabled', width: 80,
      render: (val, record) => (
        <KoSwitch checked={val} onChange={() => handleToggleRule(record)} size='small' />
      )
    },
    {
      label: '', id: 'actions', key: 'actions', width: 100,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <KoButton
            iconBtn
            icon={<KoIcon name='EditOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); ruleForm.setFieldsValue(record); setRuleModalOpen(true); }}
          />
          <KoButton
            iconBtn
            danger
            icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteOpen(true); }}
          />
        </div>
      )
    }
  ];

  const historyColumnsWithActions = [
    ...COLUMNS_ALERT_HISTORY,
    {
      label: '', id: 'actions', key: 'actions', width: 160,
      render: (_, record) => {
        if (record.status === 'resolved') return null;
        return (
          <div style={{ display: 'flex', gap: '4px' }}>
            {record.status === 'active' && (
              <KoButton label='Acknowledge' size='small' onClick={() => handleAcknowledge(record._id || record.alertId)} />
            )}
            <KoButton label='Resolve' size='small' type='primary' onClick={() => handleResolve(record._id || record.alertId)} />
          </div>
        );
      }
    }
  ];

  const tabItems = [
    { key: 'rules', label: 'Alert Rules' },
    { key: 'history', label: 'Alert History' },
    { key: 'channels', label: 'Notification Channels' }
  ];

  const channelTypeIcon = (type) => {
    const cfg = CHANNEL_TYPES.find(c => c.value === type);
    return cfg?.icon || 'MailOutlined';
  };

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <MonitoringSubNav activeKey='alerts' />

      <div className={styles.tabsWrap}>
        <KoTabs activeKey={activeTab} onChange={(key) => { setActiveTab(key); setPagination({ size: 20, page: 1 }); }} items={tabItems} />
      </div>

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <>
          <KoToolBar
            title='Alert Rules'
            search={{ visible: true, placeholder: 'Search rules...', onChange: () => {} }}
            filter={{ visible: false, options: [] }}
            buttonList={[{
              label: 'New Alert Rule',
              type: 'primary',
              icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
              onClick: () => { setCurrentRecord(null); ruleForm.resetFields(); setRuleModalOpen(true); }
            }]}
          />
          {alertRules.length === 0 && !isLoading && <KoEmpty description='No alert rules configured' />}
          {alertRules.length > 0 && (
            <>
              <KoTable maxHeight={480} dataSource={alertRules} columns={rulesColumnsWithActions} rowKey={(r) => r._id || r.alertId} />
              <KoPagination totalCount={totalRules} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
            </>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <>
          <KoToolBar title='Alert History' search={{ visible: true, placeholder: 'Search alerts...', onChange: () => {} }} filter={{ visible: false, options: [] }} buttonList={[]} />
          {alertHistory.length === 0 && !isLoading && <KoEmpty description='No alert history' />}
          {alertHistory.length > 0 && (
            <>
              <KoTable maxHeight={480} dataSource={alertHistory} columns={historyColumnsWithActions} rowKey={(r) => r._id || r.alertId} />
              <KoPagination totalCount={totalHistory} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
            </>
          )}
        </>
      )}

      {/* Channels Tab */}
      {activeTab === 'channels' && (
        <>
          <KoToolBar
            title='Notification Channels'
            search={{ visible: false }}
            filter={{ visible: false, options: [] }}
            buttonList={[{
              label: 'Add Channel',
              type: 'primary',
              icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
              onClick: () => { setCurrentRecord(null); channelForm.resetFields(); setChannelModalOpen(true); }
            }]}
          />
          {channels.length === 0 && !isLoading && <KoEmpty description='No notification channels configured' />}
          <div className={styles.channelGrid}>
            {channels.map((ch) => (
              <div key={ch._id || ch.channelId} className={styles.channelCard}>
                <div className={styles.channelCardHeader}>
                  <div className={styles.channelIconWrap}>
                    <KoIcon name={channelTypeIcon(ch.type)} width={18} height={18} />
                  </div>
                  <div className={styles.channelInfo}>
                    <div className={styles.channelName}>{ch.name}</div>
                    <div className={styles.channelType}>{ch.type}</div>
                  </div>
                </div>
                <div className={styles.channelActions}>
                  <KoButton
                    label='Test'
                    size='small'
                    onClick={() => MonitoringAPI.testAlertChannel(ch._id || ch.channelId).then(
                      () => KoToast.success('Test notification sent!'),
                      () => KoToast.error('Test failed.')
                    )}
                  />
                  <KoButton
                    label='Edit'
                    size='small'
                    onClick={() => { setCurrentRecord(ch); channelForm.setFieldsValue(ch); setChannelModalOpen(true); }}
                  />
                  <KoButton
                    label='Delete'
                    size='small'
                    danger
                    onClick={() => { setCurrentRecord(ch); setDeleteOpen(true); }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Rule Modal */}
      <KoModal
        title={currentRecord ? 'Edit Alert Rule' : 'New Alert Rule'}
        centered
        open={isRuleModalOpen}
        handleOk={() => setRuleModalOpen(false)}
        onClose={() => { setRuleModalOpen(false); setCurrentRecord(null); }}
        width={'min(700px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setRuleModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => ruleForm.submit()} />
        ]}
      >
        <AlertRuleCrud form={ruleForm} handleFinish={handleRuleSave} channels={channelOptions} services={serviceList} />
      </KoModal>

      {/* Channel Modal */}
      <KoModal
        title={currentRecord ? 'Edit Channel' : 'Add Channel'}
        centered
        open={isChannelModalOpen}
        handleOk={() => setChannelModalOpen(false)}
        onClose={() => { setChannelModalOpen(false); setCurrentRecord(null); }}
        width={'min(560px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setChannelModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => channelForm.submit()} />
        ]}
      >
        <AlertChannelCrud form={channelForm} handleFinish={handleChannelSave} />
      </KoModal>

      {/* Delete Confirmation */}
      <KoModal
        title={`Delete ${activeTab === 'rules' ? 'Alert Rule' : 'Channel'}`}
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleDeleteConfirm} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.name}"?`} />
          <KoText text='This action cannot be undone.' />
        </div>
      </KoModal>
    </div>
  );
}

export default Alerts;
